// The HEADLESS CHAT of an app (ADR 0021 amendment of 2026-10-08, ADR 0014 §11): an app that draws its own
// chat UI (a docked side panel, say) reaches the SAME chat backend as the built-in chat window, through the
// host that frames it. The app runs in the app runtime's frame on another origin and never holds the
// person's bearer, so it asks its host by postMessage; the host (cortex's Apps page, a shared app, the
// prototype preview, a project's standalone shell) makes the calls itself — the chat SDK's own routes on the
// host's api with the person's bearer and the app named on every call (`x-cortex-app`, the app-reach path) —
// and streams the answer back.
//
// What the host fixes, whatever the app asks: the project and the app (the frame it serves), and
// the persona — the Data analyst (read-only, governed reads under the data access policies; an app-only caller
// gets nothing else anyway). The app chooses only the text, the session to continue and what to save.
//
//   app → host   { source: 'cortex-app-chat', id, op, args }
//   host → app   { source: 'cortex-app-chat-event', id, event }       (turn.start: every streamed event)
//   host → app   { source: 'cortex-app-chat-reply', id, ok, result | error }
//
// The app side is `createAppChat()` of `@cortex/app-sdk/chat`.
import {
  APP_CHAT_EVENT,
  APP_CHAT_OPS,
  APP_CHAT_PROTOCOL,
  APP_CHAT_REPLY,
  APP_CHAT_REQUEST,
  type AppChatEventMessage,
  type AppChatHello,
  type AppChatMessage,
  type AppChatReply,
  type AppChatReport,
  type AppChatRequest,
  type AppChatSession,
  type AppChatTurnResult,
} from './protocol.ts';
import { sseEvents } from './turn.ts';
import { APP_REACH_HEADER } from '../adopted-names.ts';

export * from './protocol.ts';

/** The header an app's chat names its app with (the api's APP_REACH_HEADER, ADR 0014 §11). */
export { APP_REACH_HEADER };

/** The app's scope as the host reads it (its declaration through the companions route, app-reach named). */
export interface AppChatScope {
  /** The app's Chat companion is on (its declaration, minus the organization's add-ons). */
  chat: boolean;
  chatUi: 'host' | 'app';
  reach: 'project' | 'app';
  title?: string;
}

export interface AppChatBridgeOptions {
  /** The framed app's window (the iframe's contentWindow), read on every message. */
  frame: () => Window | null | undefined;
  /** The app runtime's origin: only its messages are heard, only it is answered. */
  origin: string;
  slug: string;
  app: string;
  /** The fields the host's chat turn route takes besides the chat's own (opaque here); none: none. */
  turnFields?: Readonly<Record<string, string>>;
  /** The api the chat SDK's routes are on (the host's chat mount). */
  apiBaseUrl: string;
  /** The chat service (`…/assistant`). */
  assistantBaseUrl: string;
  getToken: () => string | undefined | null;
  /** The app's scope (the companions route, named by the app). */
  scope: () => Promise<AppChatScope>;
  /** Draws a `viz` block to sanitized SVG (the host's sandbox, ADR 0013); absent: viz.render answers 501. */
  renderVizSvg?: (p: { code: string; data: unknown; width: number; height?: number }) => Promise<string>;
  /** The absolute URL of a saved report's page on the host. */
  reportUrl: (id: string) => string;
  /** Where messages are heard (default: the global window). */
  listenOn?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  fetch?: typeof fetch;
}

const MAX_MESSAGE = 20_000;
const MAX_CHARTS = 20;
const MAX_CHART_BYTES = 2_000_000;

class BridgeError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const str = (v: unknown, name: string, max = 200, optional = false): string | undefined => {
  if (v === undefined || v === null || v === '') {
    if (optional) return undefined;
    throw new BridgeError(`'${name}' is required`, 400);
  }
  if (typeof v !== 'string' || v.length > max) throw new BridgeError(`'${name}' must be a string`, 400);
  return v;
};

const isRequest = (d: unknown): d is AppChatRequest =>
  !!d &&
  typeof d === 'object' &&
  (d as AppChatRequest).source === APP_CHAT_REQUEST &&
  typeof (d as AppChatRequest).id === 'string' &&
  (d as AppChatRequest).id.length <= 100 &&
  (APP_CHAT_OPS as readonly string[]).includes((d as AppChatRequest).op);

/** The charts a report keeps: chart and viz blocks only, each a plain object of bounded size. */
function chartsOf(v: unknown): Record<string, unknown>[] {
  if (!Array.isArray(v) || v.length === 0 || v.length > MAX_CHARTS)
    throw new BridgeError(`'charts' must hold 1 to ${MAX_CHARTS} charts`, 400);
  return v.map((c, i) => {
    if (!c || typeof c !== 'object' || Array.isArray(c))
      throw new BridgeError(`charts[${i}] is not a chart`, 400);
    const t = (c as { type?: unknown }).type;
    if (t !== 'chart' && t !== 'viz') throw new BridgeError(`charts[${i}] is not a chart or a viz`, 400);
    if (JSON.stringify(c).length > MAX_CHART_BYTES) throw new BridgeError(`charts[${i}] is too large`, 413);
    return { ...(c as Record<string, unknown>) };
  });
}

/**
 * Serves the headless chat to one framed app. Answers the stop function (the frame went away). The host
 * mounts it beside its iframe for as long as the app is open.
 */
export function serveAppChat(o: AppChatBridgeOptions): () => void {
  const listenOn = o.listenOn ?? window;
  const fetchImpl = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const turns = new Map<string, AbortController>();
  let scopeP: Promise<AppChatScope> | null = null;
  const scope = (fresh = false) => {
    if (fresh || !scopeP) scopeP = o.scope();
    return scopeP;
  };
  const post = (m: AppChatReply | AppChatEventMessage) => {
    try {
      o.frame()?.postMessage(m, o.origin);
    } catch {
      /* the frame navigated away */
    }
  };
  const api = o.apiBaseUrl.replace(/\/+$/, '');
  const project = `/api/assistant/projects/${encodeURIComponent(o.slug)}`;
  /** A call of the chat's own routes: the person's bearer, the app named. */
  async function rest<T>(method: string, path: string, what: string, body?: unknown): Promise<T> {
    const token = o.getToken();
    const res = await fetchImpl(`${api}${project}${path}`, {
      method,
      headers: {
        accept: 'application/json',
        [APP_REACH_HEADER]: o.app,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!res.ok) {
      const m = (data as { message?: unknown } | null)?.message;
      throw new BridgeError(typeof m === 'string' ? m : `${what} answered ${res.status}`, res.status);
    }
    return data as T;
  }
  const enc = encodeURIComponent;

  const hello = async (): Promise<AppChatHello> => {
    const base = {
      protocol: APP_CHAT_PROTOCOL,
      project: o.slug,
      app: o.app,
      persona: 'analyst' as const,
      viz: !!o.renderVizSvg,
      stream: true,
    };
    try {
      const s = await scope(true);
      return {
        ...base,
        available: s.chat,
        ...(s.chat ? {} : { reason: "This app's chat is turned off" }),
        ...(s.title ? { title: s.title } : {}),
        reach: s.reach,
        chatUi: s.chatUi,
      };
    } catch (e) {
      return {
        ...base,
        available: false,
        reason: `The chat could not be reached: ${(e as Error).message}`,
        reach: 'project',
        chatUi: 'app',
      };
    }
  };

  const mustBeOn = async () => {
    const s = await scope().catch(() => null);
    if (!s?.chat) throw new BridgeError("This app's chat is turned off", 403);
  };

  async function startTurn(id: string, args: Record<string, unknown>): Promise<AppChatTurnResult> {
    const message = str(args.message, 'message', MAX_MESSAGE)!.trim();
    if (!message) throw new BridgeError("'message' is empty", 400);
    const sessionId = str(args.sessionId, 'sessionId', 100, true);
    const historySince = str(args.historySince, 'historySince', 40, true);
    const stream = args.stream === true;
    const ac = new AbortController();
    turns.set(id, ac);
    const token = o.getToken();
    const result: AppChatTurnResult = { status: 'error', sessionId: sessionId ?? null, text: '', blocks: [] };
    try {
      const res = await fetchImpl(
        `${o.assistantBaseUrl.replace(/\/+$/, '')}/v1/projects/${encodeURIComponent(o.slug)}/chat`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            ...o.turnFields,
            message,
            persona: 'analyst',
            app: o.app,
            ...(sessionId ? { sessionId } : {}),
            ...(sessionId && historySince ? { historySince } : {}),
            ...(stream ? { stream: true } : {}),
          }),
          signal: ac.signal,
        },
      );
      if (!res.ok || !res.body)
        throw new BridgeError(
          res.status === 401 ? 'Your session has ended; sign in again.' : `The chat answered ${res.status}`,
          res.status || 502,
        );
      for await (const event of sseEvents(res.body)) {
        if (event.type === 'session') result.sessionId = event.sessionId;
        if (event.type === 'done') {
          result.status = event.status;
          result.text = event.text;
          result.blocks = event.blocks;
        }
        if (event.type === 'error') result.status = 'error';
        post({ source: APP_CHAT_EVENT, id, event });
      }
      return result;
    } catch (e) {
      if (ac.signal.aborted) return { ...result, status: 'stopped' };
      throw e;
    } finally {
      turns.delete(id);
    }
  }

  async function run(req: AppChatRequest): Promise<unknown> {
    const a = req.args ?? {};
    if (req.op === 'hello') return hello();
    if (req.op === 'turn.stop') {
      turns.get(str(a.turn, 'turn', 100)!)?.abort();
      return { stopped: true };
    }
    await mustBeOn();
    switch (req.op) {
      case 'sessions.list': {
        const r = await rest<{ sessions?: AppChatSession[] }>('GET', '/sessions', 'the sessions');
        return (r?.sessions ?? []).map((x) => ({
          id: x.id,
          title: x.title,
          ...(x.createdAt ? { createdAt: x.createdAt } : {}),
          ...(x.updatedAt ? { updatedAt: x.updatedAt } : {}),
        }));
      }
      case 'sessions.get': {
        const id = str(a.id, 'id', 100)!;
        return rest<{ session: AppChatSession; messages: AppChatMessage[] }>(
          'GET',
          `/sessions/${enc(id)}`,
          'the session',
        );
      }
      case 'sessions.delete': {
        const id = str(a.id, 'id', 100)!;
        await rest('DELETE', `/sessions/${enc(id)}`, 'deleting the session');
        return { deleted: true };
      }
      case 'turn.start':
        return startTurn(req.id, a);
      case 'reports.save': {
        const answer = str(a.answer, 'answer', 200_000)!;
        const userPrompt = str(a.question, 'question', MAX_MESSAGE, true);
        const charts = chartsOf(a.charts);
        const saved = await rest<{ id: string }>('POST', '/reports', 'saving the report', {
          ...(userPrompt ? { userPrompt } : {}),
          answer,
          charts,
        });
        return { id: saved.id, url: o.reportUrl(saved.id) };
      }
      case 'reports.list': {
        const r = await rest<{
          reports?: { id: string; title: string; createdAt: string; chartCount: number }[];
        }>('GET', '/reports', 'the reports');
        return (r?.reports ?? []).map((x): AppChatReport => ({
          id: x.id,
          title: x.title,
          createdAt: x.createdAt,
          chartCount: x.chartCount,
          url: o.reportUrl(x.id),
        }));
      }
      case 'reports.delete': {
        const id = str(a.id, 'id', 200)!;
        await rest('DELETE', `/reports/${enc(id)}`, 'deleting the report');
        return { deleted: true };
      }
      case 'viz.render': {
        if (!o.renderVizSvg) throw new BridgeError('This host does not draw visualizations', 501);
        const code = str(a.code, 'code', 200_000)!;
        const width = typeof a.width === 'number' && a.width > 0 ? Math.min(4000, Math.round(a.width)) : 640;
        const height =
          typeof a.height === 'number' && a.height > 0 ? Math.min(4000, Math.round(a.height)) : undefined;
        const svg = await o.renderVizSvg({
          code,
          data: a.data ?? null,
          width,
          ...(height ? { height } : {}),
        });
        return { svg };
      }
      default:
        throw new BridgeError(`Unknown operation '${String(req.op)}'`, 400);
    }
  }

  const onMessage = (e: Event) => {
    const m = e as MessageEvent;
    const frame = o.frame();
    if (!frame || m.source !== frame || m.origin !== o.origin || !isRequest(m.data)) return;
    const req = m.data;
    run(req).then(
      (result) => post({ source: APP_CHAT_REPLY, id: req.id, ok: true, result }),
      (err: unknown) =>
        post({
          source: APP_CHAT_REPLY,
          id: req.id,
          ok: false,
          error: {
            message: err instanceof Error ? err.message : String(err),
            status: err instanceof BridgeError ? err.status : 502,
          },
        }),
    );
  };
  listenOn.addEventListener('message', onMessage);
  return () => {
    listenOn.removeEventListener('message', onMessage);
    for (const ac of turns.values()) ac.abort();
    turns.clear();
  };
}

/** The scope of an app from the companions route's answer (a host's `scope` reads the route, then this). */
export function appChatScopeOf(
  r:
    | {
        companions?: { key: string }[];
        chatUi?: string;
        reach?: string;
        title?: string;
      }
    | null
    | undefined,
): AppChatScope {
  return {
    chat: !!r?.companions?.some((c) => c.key === 'chat'),
    // Every host draws no chat of its own over an app any more (companion UI is off for every app, 2026-11-04): the
    // app's own UI is the only chat there is.
    chatUi: 'app',
    reach: r?.reach === 'app' ? 'app' : 'project',
    ...(r?.title ? { title: r.title } : {}),
  };
}
